import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Consultant from './Consultant';
import * as currency from '@shopify/theme-currency';
import { getSizedImageUrl } from '@shopify/theme-images';
import axios from 'axios';

import CartItem from './CartItem';
import CartBundleItem from './CartBundleItem';
import ChevronDown from './ChevronDown';
import { includes } from 'lodash';
import { pushToCartEvent } from '../ga4-events-scripts.js';
import { getFromStorage } from '../utils/storage.js';

const CUSTOMER_TYPE = window.theme && window.theme.customerType ? window.theme.customerType : null;
const CUSTOMER_DISCOUNTS = {
  consultant: 'consultant_discount:',
  team_lead: 'team_leader_discount:',
  staff: 'staff_discount:',
};
const eventBus = require('js-event-bus')();
function FreeShippingBar({ total, threshold }) {
  let barPercentege = (total / threshold) * 100;
  const leftAmount = threshold - total;
  const message =
    leftAmount <= 0
      ? window.theme.freeShipping.messageOver
      : window.theme.freeShipping.messageUnder.replace(
        '{{ amount }}',
        currency.formatMoney(leftAmount, window.theme.moneyFormat)
      );

  if (barPercentege > 100) {
    barPercentege = 100;
  }

  const barWidth = {
    width: `${barPercentege}%`,
  };

  return (
    <div className="side-cart__free-shipping">
      <div className="side-cart__progress">
        <div className={`bar${barPercentege === 100 ? ' bar--full' : ''}`} style={barWidth} />
      </div>
      <p dangerouslySetInnerHTML={{ __html: message }} />
    </div>
  );
}

const getUpsellProducts = async (cartItems, upsellsHandles, maxProducts, setProducts) => {
  let upsellsProducts = [];

  if (upsellsHandles.length > 0) {
    // get upsell products using ajax api...
    for (const handle of upsellsHandles) {
      // first check sessionStorage to see if we already have this product
      const upsellsProduct = sessionStorage.getItem(`__upsell:${handle}`);
      if (upsellsProduct) {
        if (JSON.parse(upsellsProduct).available) {
          upsellsProducts = [...upsellsProducts, JSON.parse(upsellsProduct)];
        }
        continue;
      }
      // get the product data via the ajax api
      const response = await axios({
        method: 'GET',
        url: `/products/${handle}.js`,
        responseType: 'JSON',
      });
      sessionStorage.setItem(`__upsell:${handle}`, JSON.stringify(response.data));
      if (response.data.available) {
        upsellsProducts = [...upsellsProducts, response.data];
      }
    }
  }

  if (upsellsHandles.length < maxProducts) {
    // get upsell products using the product recommendations api...
    const mostExpensiveProduct = cartItems.reduce((prev, current) =>
      prev.final_price > current.final_price ? prev : current
    );
    const productHandle = mostExpensiveProduct.handle;
    const limit = maxProducts - upsellsProducts.length;
    // first check sessionStorage to see if we already have this product
    let upsellsRecommendations = sessionStorage.getItem(`__recommendations:${productHandle}`);
    if (upsellsRecommendations) {
      upsellsRecommendations = JSON.parse(upsellsRecommendations);
    } else {
      const productId = mostExpensiveProduct.product_id;
      const response = await axios({
        method: 'GET',
        url: `/recommendations/products.json?product_id=${productId}&limit=10`,
        responseType: 'JSON',
      });
      upsellsRecommendations = response.data.products;
      sessionStorage.setItem(`__recommendations:${productHandle}`, JSON.stringify(upsellsRecommendations));
    }
    /**
     * remove any products already in the cart, already in the upsells section or that are tagged with any of the following:
     *   - `consultant_only`
     *   - `Coming Soon`
     *   - `tm_machine`
     */
    upsellsRecommendations = upsellsRecommendations.filter(({ handle, tags }) => {
      const isInCart = cartItems.some(item => item.handle === handle);
      const isInUpsells = upsellsHandles.some(upsell => upsell === handle);
      const isConsultantOnly = tags.includes('consultant_only');
      const isHostRewards = tags.includes('host_rewards');
      const isComingSoon = tags.includes('Coming Soon');
      const isMachine = tags.includes('tm_machine');
      return !isInCart && !isInUpsells && !isConsultantOnly && !isHostRewards && !isComingSoon && !isMachine;
    });
    upsellsProducts = [...upsellsProducts, ...upsellsRecommendations.slice(0, limit)];
  }

  /**
   * Check if this customer is entitled to a discount based on their customer type and the products tags
   * Discounts apply to consultants, team leads and staff
   */
  if (CUSTOMER_TYPE in CUSTOMER_DISCOUNTS) {
    upsellsProducts = upsellsProducts.map(upsellsProduct => {
      const discountTag = upsellsProduct.tags.find(tag => tag.includes(CUSTOMER_DISCOUNTS[CUSTOMER_TYPE]));
      if (discountTag) {
        const discountPercentage = Number(discountTag.split(':')[1]);
        const price =
          discountPercentage > 0 ? ((100 - discountPercentage) * upsellsProduct.price) / 100 : upsellsProduct.price;
        const priceMin =
          discountPercentage > 0
            ? ((100 - discountPercentage) * upsellsProduct.price_min) / 100
            : upsellsProduct.price_min;
        const compareAtPrice = discountPercentage > 0 ? upsellsProduct.price : upsellsProduct.compare_at_price;
        upsellsProduct.price = price;
        upsellsProduct.price_min = priceMin;
        upsellsProduct.compare_at_price = compareAtPrice;
      }
      return upsellsProduct;
    });
  }

  /**
   * Update any upsells that have a manually defined discount
   */
  upsellsProducts = upsellsProducts.map(upsellsProduct => {
    const upsellDetails = window.theme.cartUpsells.products.find(({ upsellProduct, conditionalProduct }) => {
      const productMatch = upsellProduct === upsellsProduct.handle;
      const conditionalMatch = conditionalProduct ? cartItems.some(item => item.handle === conditionalProduct) : true;
      return productMatch && conditionalMatch;
    });
    if (upsellDetails && upsellDetails.conditionalDiscount) {
      if (upsellDetails.conditionalDiscount.includes('%')) {
        const discountPercentage = Number.parseFloat(upsellDetails.conditionalDiscount);
        const price =
          discountPercentage > 0 ? ((100 - discountPercentage) * upsellsProduct.price) / 100 : upsellsProduct.price;
        const priceMin =
          discountPercentage > 0
            ? ((100 - discountPercentage) * upsellsProduct.price_min) / 100
            : upsellsProduct.price_min;
        const compareAtPrice = discountPercentage > 0 ? upsellsProduct.price : upsellsProduct.compare_at_price;
        upsellsProduct.price = price;
        upsellsProduct.price_min = priceMin;
        upsellsProduct.compare_at_price = compareAtPrice;
      } else {
        let discountAmount = Number.parseFloat(upsellDetails.conditionalDiscount) * 100;
        if (discountAmount < 0) {
          discountAmount *= -1;
        }
        if (discountAmount > upsellsProduct.price) {
          discountAmount = upsellsProduct.price;
        }
        const price = upsellsProduct.price - discountAmount;
        const priceMin = upsellsProduct.price_min - discountAmount;
        const compareAtPrice = upsellsProduct.price;
        upsellsProduct.price = price;
        upsellsProduct.price_min = priceMin;
        upsellsProduct.compare_at_price = compareAtPrice;
      }
    }
    return upsellsProduct;
  });

  setProducts(upsellsProducts);
};

const addUpsellProducts = async (autoAddUpsellsProducts, handleAddToCartEvent) => {
  if (!autoAddUpsellsProducts.length) {
    return;
  }

  let upsellsProducts = [];

  // get upsell products using ajax api...
  for (const { upsellProduct } of autoAddUpsellsProducts) {
    // first check sessionStorage to see if we already have this product
    const upsellsProduct = sessionStorage.getItem(`__upsell:${upsellProduct}`);
    if (upsellsProduct) {
      if (JSON.parse(upsellsProduct).available) {
        upsellsProducts = [...upsellsProducts, JSON.parse(upsellsProduct)];
      }
      continue;
    }
    // get the product data via the ajax api
    const response = await axios({
      method: 'GET',
      url: `/products/${upsellProduct}.js`,
      responseType: 'JSON',
    });
    sessionStorage.setItem(`__upsell:${upsellProduct}`, JSON.stringify(response.data));
    if (response.data.available) {
      upsellsProducts = [...upsellsProducts, response.data];
    }
  }

  const items = upsellsProducts.map(({ variants, tags, handle }) => {
    const preorderTag = tags.find(tag => tag.startsWith('preorder'));
    const preorderDate = preorderTag && preorderTag.includes(':') ? preorderTag.split(':')[1] : null;
    const upsellDetails = autoAddUpsellsProducts.find(({ upsellProduct }) => upsellProduct === handle);
    const item = {
      id: variants[0].id,
      quantity: 1,
      properties: {
        _upsell: handle,
        _autoupsell: true,
      },
    };
    if (preorderTag) {
      item.properties.Preorder = preorderDate ? preorderDate : true;
    }
    if (upsellDetails && upsellDetails.conditionalDiscount) {
      item.properties._upsell_conditional_id = upsellDetails.conditionalProductId;
      item.properties._upsell_conditional_discount = upsellDetails.conditionalDiscount;
      item.properties._upsell_conditional_hash = upsellDetails.conditionalHash;
    }
    return item;
  });
  if (!items) {
    return;
  }

  handleAddToCartEvent(
    items,
    false,
    0,
    '',
    window.location.pathname == '/cart' ? () => window.location.reload() : null,
  );
};

function CartUpsells({ cartItems, upsellsProducts, isCollapsed, toggleUpsellsCollapse, handleAddToCartEvent }) {
  const scrollContainer = useRef(null);
  const maxNumberOfUpsellsToShow = 5;
  let maxNumberOfUpsells = maxNumberOfUpsellsToShow;
  const [products, setProducts] = useState([]);
  const upsellsClass = isCollapsed ? 'cart-upsells cart-upsells--is-collapsed' : 'cart-upsells';
  const removedAutoUpsells =
    window.localStorage.getItem('removedAutoUpsell') && window.localStorage.getItem('removedAutoUpsell') > Date.now();

  /**
   * Handle slider arrow clicks
   */
  function handleClick(slidesToScroll) {
    const container = scrollContainer.current;
    const slideWidth = 320;
    container.scrollLeft = container.scrollLeft + slidesToScroll * slideWidth;
  }

  /**
   * Find any generic upsells that don't require a specific product in the customers cart
   */
  let genericUpsellsProducts = upsellsProducts.filter(upsellsProduct => {
    const upsellProductInCart = cartItems.some(cartItem => cartItem.handle === upsellsProduct.upsellProduct);
    const noConditions = !upsellsProduct.conditionalProduct;
    return !upsellProductInCart && noConditions;
  });

  /**
   * Find upsells that match specific products in the customers cart
   */
  let specificUpsellsProducts = upsellsProducts.filter(upsellsProduct =>
    cartItems.some(cartItem => cartItem.handle === upsellsProduct.conditionalProduct && ((upsellsProduct.isBundle == true && upsellsProduct.isAvailable == true) || upsellsProduct.isBundle == false) && (!cartItem.properties.hasOwnProperty('_disable_pair_and_save') || cartItem.properties['_disable_pair_and_save'] == 'false'))
  );

  /**
   * Check if any of the conditional upsells have the limit to one upsell option set
   */
  const limitToOneUpsell = specificUpsellsProducts.some(
    upsellsProduct => upsellsProduct.conditionalProduct && upsellsProduct.conditionalLimit
  );

  /**
   * Check if any of the conditional upsell products are already in the cart
   */
  const anyUpsellProductInCart = specificUpsellsProducts.some(upsellsProduct =>
    cartItems.some(cartItem => cartItem.handle === upsellsProduct.upsellProduct)
  );

  /**
   * Find a specific upsell heading if one exists
   */
  const specificUpsellsProductWithHeading = specificUpsellsProducts.find(({ conditionalHeading }) => conditionalHeading);

  /**
   * Remove any auto upsell products from specificUpsellsProducts list
   * as long as 'removedAutoUpsell' is not set in localStorage
   */
  if (!removedAutoUpsells) {
    specificUpsellsProducts = specificUpsellsProducts.filter(upsellsProduct => !upsellsProduct.conditionalAutoAdd);
  }

  /**
   * If any of the upsell products are in the cart we need to update the upsells to show accordingly
   */
  if (anyUpsellProductInCart) {
    if (limitToOneUpsell) {
      specificUpsellsProducts = [];
    } else {
      specificUpsellsProducts = specificUpsellsProducts.filter(upsellsProduct => {
        const upsellProductInCart = cartItems.some(cartItem => cartItem.handle === upsellsProduct.upsellProduct);
        return !upsellProductInCart;
      });
    }
    maxNumberOfUpsells =
      specificUpsellsProducts.length > maxNumberOfUpsellsToShow
        ? maxNumberOfUpsellsToShow
        : specificUpsellsProducts.length;
  }

  /**
   * Build a final list of handles
   * If there are upsells related to specific products in the customsers cart
   * we only want to include these. If there are no specific products we will
   * show the generic upsells along with results from the product recommendations
   * API up to a limit defined by `maxNumberOfUpsells`.
   */
  const upsellsHandles =
    specificUpsellsProducts.length > 0
      ? specificUpsellsProducts.map(upsellsProduct => upsellsProduct.upsellProduct).slice(0, maxNumberOfUpsells)
      : genericUpsellsProducts.map(upsellsProduct => upsellsProduct.upsellProduct).slice(0, maxNumberOfUpsells);

  const numberOfUpsellsToShow = specificUpsellsProducts.length > 0 ? upsellsHandles.length : maxNumberOfUpsells;

  const upsellsHeading =
    specificUpsellsProducts.length > 0 && specificUpsellsProductWithHeading
      ? specificUpsellsProductWithHeading.conditionalHeading
      : window.theme.cartUpsells.heading;

  /**
   * Find upsells that match specific products in the customers cart and need to be automatically added to cart
   */
  let autoAddUpsellsProducts =
    limitToOneUpsell && anyUpsellProductInCart
      ? []
      : upsellsProducts.filter(upsellsProduct => {
        const conditionalProductInCart = cartItems.some(
          cartItem => cartItem.handle === upsellsProduct.conditionalProduct && (!cartItem.properties.hasOwnProperty('_disable_gwp') || cartItem.properties['_disable_gwp'] == 'false')
        );
        const upsellProductInCart = cartItems.some(cartItem => cartItem.handle === upsellsProduct.upsellProduct);
        const autoAddUpsellProduct = upsellsProduct.conditionalAutoAdd;
        return conditionalProductInCart && !upsellProductInCart && autoAddUpsellProduct;
      });

  if (autoAddUpsellsProducts.length > 1 && limitToOneUpsell) {
    autoAddUpsellsProducts = autoAddUpsellsProducts.slice(0, 1);
  }

  useEffect(() => {
    if (!removedAutoUpsells) {
      addUpsellProducts(autoAddUpsellsProducts, handleAddToCartEvent);
    }
    getUpsellProducts(cartItems, upsellsHandles, numberOfUpsellsToShow, setProducts);
  }, [cartItems]);

  if (products.length === 0) {
    return '';
  }

  return (
    <div className="side-cart__upsells">
      <div className={upsellsClass}>
        <div className="cart-upsells__heading" onClick={() => toggleUpsellsCollapse()}>
          {upsellsHeading}
          <ChevronDown />
        </div>
        <div className="cart-upsells__items" ref={scrollContainer}>
          {products.map(product => (
            <CartUpsell
              key={product.id}
              {...product}
              cartItems={cartItems}
              handleAddToCartEvent={handleAddToCartEvent}
            />
          ))}
        </div>
        {products.length > 1 && (
          <>
            <button
              type="button"
              className="cart-upsells__nav-button cart-upsells__nav-button--prev"
              onClick={() => handleClick(-1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="cart-upsells__nav-button cart-upsells__nav-button--next"
              onClick={() => handleClick(1)}
            >
              Next
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CartUpsell({
  featured_image,
  title,
  handle,
  url,
  price,
  price_min,
  compare_at_price,
  price_varies,
  variants,
  tags,
  handleAddToCartEvent,
  cartItems,
}) {
  const [disabled, setDisabled] = useState(false);
  const preorderTag = tags.find(tag => tag.startsWith('preorder'));
  const preorderDate = preorderTag && preorderTag.includes(':') ? preorderTag.split(':')[1] : null;
  const hasDiscount = compare_at_price > price;
  const firstAvailableVariant = variants.find(({ available }) => available);
  const upsellDetails = window.theme.cartUpsells.products.find(({ upsellProduct, conditionalProduct }) => {
    const productMatch = upsellProduct === handle;
    const conditionalMatch = conditionalProduct ? cartItems.some(item => item.handle === conditionalProduct) : true;
    return productMatch && conditionalMatch;
  });

  let isBundle = false;
  let productObj = {};
  window.theme.cartUpsellProducts = window.theme.cartUpsellProducts || [];

  axios({
    method: 'GET',
    url: `/products/${handle}.js`,
    responseType: 'JSON',
  }).then(response => {
    productObj = response.data;
    // Push these objects to window.theme.cartUpsellProducts for ga4-access
    window.theme.cartUpsellProducts.push(productObj)
    let productTags = productObj.tags;
    isBundle = productTags.includes('bundle') ? true : false;
  });

  let variantToAdd = firstAvailableVariant.id;
  if (upsellDetails && upsellDetails.upsellVariant != 'default') {
    variantToAdd = parseInt(upsellDetails.upsellVariant);
  }
  const item = {
    id: variantToAdd,
    quantity: 1,
    properties: {
      _upsell: handle,
    },
  };
  if (preorderTag) {
    item.properties.Preorder = preorderDate ? preorderDate : true;
  }
  if (upsellDetails && upsellDetails.conditionalDiscount) {
    item.properties._upsell_conditional_id = upsellDetails.conditionalProductId;
    item.properties._upsell_conditional_discount = upsellDetails.conditionalDiscount;
    item.properties._upsell_conditional_hash = upsellDetails.conditionalHash;
    item.properties._upsell_variant = upsellDetails.upsellVariant;
  }
  const items = [item];

  if (!featured_image) {
    return <></> // Don't render anything if featured_image is missing
  }

  return (
    <div className="cart-upsells__item">
      <div className="product-card">
        <div className="product-card__image">
          {hasDiscount && (
            <div className="card__badge">
              <span className="badge badge-sale">Sale</span>          
            </div>
          )}
          {featured_image && (
            <img loading="lazy" src={getSizedImageUrl(featured_image, '160x160_crop_center')} alt={title} />
          )}
        </div>
        <div className="product-card__content">
          <h4 className="product-card__title">
            <a href={url}>{title}</a>
          </h4>
          <div className={`product-card__price ${hasDiscount ? 'product-card__price--discounted' : ''}`}>
            {price_varies ? (
              <span>
                {window.theme.strings.priceFrom.replace(
                  '{{ price }}',
                  currency.formatMoney(price_min, window.theme.moneyFormat)
                )}
              </span>
            ) : (
              <>
                {hasDiscount && <s>{currency.formatMoney(compare_at_price, window.theme.moneyFormat)}</s>}
                <span data-product-price>{currency.formatMoney(price, window.theme.moneyFormat)}</span>
              </>
            )}
          </div>
          {Boolean(firstAvailableVariant) && (
            <div className="product-card__cta">
              <button
                className="btn btn--secondary btn--xsmall"
                onClick={() => {
                  setDisabled(true);
                  handleAddToCartEvent(items, false, 0, handle, null, isBundle, productObj);
                }}
                disabled={disabled}
              >
                {window.theme.strings.addToCart}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PairAndSave = ({
  discountMultiplier,
  pairedContent,
  pairedHeading,
  pairedProduct,
  pairedProductDiscount,
  handleAddToCartEvent,
}) => {
  const { title, variants, featured_image, price, preorder, preorderDate } = pairedProduct;

  const [disabled, setDisabled] = useState(false);
  const hasDiscount = pairedProductDiscount > 0;
  const originalprice = price;
  const discountedPrice = price * discountMultiplier;
  const firstAvailableVariant = variants.find(({ available }) => available);
  const item = {
    quantity: 1,
    id: firstAvailableVariant.id,
  };
  if (preorder) {
    item.properties = {
      Preorder: preorderDate,
    };
  }
  return (
    <div className="cart-upsells__item">
      <div className="product-card">
        <div className="product-card__image">
          {featured_image && (
            <img loading="lazy" src={getSizedImageUrl(featured_image, '160x160_crop_center')} alt={title} />
          )}
        </div>
        <div className="product-card__content">
          <h4 className="product-card__title">{pairedHeading}</h4>
          <p className="product-card__paragraph">{pairedContent}</p>
          <div className={`product-card__price ${hasDiscount ? 'product-card__price--discounted' : ''}`}>
            {hasDiscount && <s>{currency.formatMoney(originalprice, window.theme.moneyFormat)}</s>}
            <span data-product-price>{currency.formatMoney(discountedPrice, window.theme.moneyFormat)}</span>
          </div>
          {Boolean(firstAvailableVariant) && (
            <div className="product-card__cta">
              <button
                className="btn btn--outline btn--xsmall"
                onClick={() => {
                  setDisabled(true);
                  handleAddToCartEvent([item]);
                }}
                disabled={disabled}
              >
                {window.theme.strings.addToCart}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PairAndSave.propTypes = {
  discountMultiplier: PropTypes.number,
  pairedContent: PropTypes.string,
  pairedHeading: PropTypes.string,
  pairedProduct: PropTypes.object,
  pairedProductDiscount: PropTypes.number,
  productId: PropTypes.number,
  handleAddToCartEvent: PropTypes.func,
};

const PairAndSaves = ({
  cartItems,
  isCollapsed,
  handleTogglePairAndSaveCollapse,
  toggleUpsellsCollapse,
  handleAddToCartEvent,
}) => {
  const scrollContainer = useRef(null);
  const upsellsClass = isCollapsed ? 'cart-upsells cart-upsells--is-collapsed' : 'cart-upsells';
  const [products, setProducts] = useState([]);

  /**
   * Handle slider arrow clicks
   */
  function handleClick(slidesToScroll) {
    const container = scrollContainer.current;
    const slideWidth = 320;
    container.scrollLeft = container.scrollLeft + slidesToScroll * slideWidth;
  }

  useEffect(() => {
    (async () => {
      try {
        const pairAndSaveResponse = await fetch('/cart?view=pair-and-save');
        const pairAndSaveData = await pairAndSaveResponse.json();

        /**
         * Firstly filter out cart items that do not have a paired product metafield.
         * Then filter out any paired products that are already in the cart.
         */
        const pairAndSaveProducts = pairAndSaveData
          .filter(product => product.pairedProduct)
          .filter(product => !cartItems.some(cartItem => cartItem.product_id === product.pairedProduct.id));

        if (!pairAndSaveProducts.length) {
          setProducts([]);
          return;
        }

        /**
         * Close the upsell accordion if there is a Pair and Save accordion.
         */
        toggleUpsellsCollapse(true);
        setProducts(pairAndSaveProducts);
      } catch (e) {
        window.console.error('Error setting Pair and Save Products: ', e);
      }
    })();
  }, [cartItems]);

  return products.length ? (
    <div className="side-cart__upsells side-cart__pair-and-save">
      <div className={upsellsClass}>
        <div
          className="cart-upsells__heading"
          onClick={() => {
            handleTogglePairAndSaveCollapse();
          }}
        >
          {'Pair And Save'}
          <ChevronDown />
        </div>
        <div className="cart-upsells__items" ref={scrollContainer}>
          {products.map(product => (
            <PairAndSave key={product.productId} {...product} handleAddToCartEvent={handleAddToCartEvent} />
          ))}
        </div>
        {products.length > 1 && (
          <>
            <button
              type="button"
              className="cart-upsells__nav-button cart-upsells__nav-button--prev"
              onClick={() => handleClick(-1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="cart-upsells__nav-button cart-upsells__nav-button--next"
              onClick={() => handleClick(1)}
            >
              Next
            </button>
          </>
        )}
      </div>
    </div>
  ) : null;
};

PairAndSaves.propTypes = {
  cartItems: PropTypes.array,
  isCollapsed: PropTypes.bool,
  handleTogglePairAndSaveCollapse: PropTypes.func,
  toggleUpsellsCollapse: PropTypes.func,
  handleAddToCartEvent: PropTypes.func,
};

const BundleAndSave = ({
  parentBundleProduct,
  parentBundleHeading,
  parentBundleContent,
  handleBundleAndSaveAddToCartEvent
}) => {
  const { title, variants, featured_image, price, compare_at_price, preorder, preorderDate } = parentBundleProduct;

  const [disabled, setDisabled] = useState(false);
  const originalprice = compare_at_price;
  const discountedprice = price;
  const firstAvailableVariant = variants.find(({ available }) => available);
  const item = {
    quantity: 1,
    id: firstAvailableVariant.id,
  };
  if (preorder) {
    item.properties = {
      Preorder: preorderDate,
    };
  }
  return (
    <div className="cart-upsells__item">
      <div className="product-card">
        <div className="product-card__image">
          {featured_image && (
            <img loading="lazy" src={getSizedImageUrl(featured_image, '160x160_crop_center')} alt={title} />
          )}
        </div>
        <div className="product-card__content">
          <h4 className="product-card__title">{parentBundleHeading}</h4>
          <p className="product-card__paragraph">{parentBundleContent}</p>
          <div className={`product-card__price ${originalprice > discountedprice ? 'product-card__price--discounted' : ''}`}>
            <div className="price-col">
              {originalprice > discountedprice && <s>{currency.formatMoney(originalprice, window.theme.moneyFormat)}</s>}
              <span data-product-price>{currency.formatMoney(discountedprice, window.theme.moneyFormat)}</span>
            </div>
            {originalprice > discountedprice &&
              <div className="savings-col">
                <p className="savings-text">Save <span>{`${currency.formatMoney(originalprice - discountedprice, window.theme.moneyFormat)}`}</span> off the total<br />BUNDLE &amp; SAVE</p>
              </div>
            }
          </div>
          {Boolean(firstAvailableVariant) && (
            <div className="product-card__cta">
              <button
                className="btn btn--outline btn--xsmall"
                onClick={() => {
                  setDisabled(true);
                  handleBundleAndSaveAddToCartEvent([item]);
                }}
                disabled={disabled}
              >
                {window.theme.strings.addToCart}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BundleAndSaves = ({
  cartItems,
  isCollapsed,
  handleToggleBundleAndSaveCollapse,
  toggleUpsellsCollapse,
  handleBundleAndSaveAddToCartEvent,
  setBundleAndSaveItems
}) => {
  const scrollContainer = useRef(null);
  const upsellsClass = isCollapsed ? 'cart-upsells cart-upsells--is-collapsed' : 'cart-upsells';
  const [products, setProducts] = useState([]);
  let bundleProductsIDArray = [];

  /**
   * Handle slider arrow clicks
   */
  function handleClick(slidesToScroll) {
    const container = scrollContainer.current;
    const slideWidth = 320;
    container.scrollLeft = container.scrollLeft + slidesToScroll * slideWidth;
  }

  useEffect(() => {
    (async () => {
      try {
        const bundleAndSaveResponse = await fetch('/cart?view=bundle-and-save');
        const bundleAndSaveData = await bundleAndSaveResponse.json();

        /**
         * Firstly filter out cart items that do not have a paired product metafield.
         * Then filter out any paired products that are already in the cart.
         */
        const bundleAndSaveProducts = bundleAndSaveData
          .filter(product => product.parentBundleProduct)
          .filter(product => !cartItems.some(cartItem => cartItem.product_id === product.parentBundleProduct.id));

        let bundleObjectArray = [];
        let bundleRenderObjects = [];
        bundleAndSaveProducts.map((bundleObj) => {
          bundleObjectArray.push(bundleObj);
          if (!bundleProductsIDArray.includes(bundleObj.parentBundleProduct.id)) {
            bundleProductsIDArray.push(bundleObj.parentBundleProduct.id);
            bundleRenderObjects.push(bundleObj);
          }
        });

        if (!bundleAndSaveProducts.length) {
          setProducts([]);
          return;
        }

        /**
         * Close the upsell accordion if there is a Pair and Save accordion.
         */
        toggleUpsellsCollapse(true);
        setBundleAndSaveItems(bundleObjectArray);
        setProducts(bundleRenderObjects);
      } catch (e) {
        window.console.error('Error setting Bundle and Save Items: ', e);
      }
    })();
  }, [cartItems]);
  return products.length ? (
    <div className="side-cart__upsells side-cart__pair-and-save side-cart__bunndle-and-save">
      <div className={upsellsClass}>
        <div
          className="cart-upsells__heading"
          onClick={() => {
            handleToggleBundleAndSaveCollapse();
          }}
        >
          {'Bundle And Save'}
          <ChevronDown />
        </div>
        <div className="cart-upsells__items" ref={scrollContainer}>
          {products.map(product => (
            <BundleAndSave key={product.productId} {...product} handleBundleAndSaveAddToCartEvent={handleBundleAndSaveAddToCartEvent} />
          ))}
        </div>
        {products.length > 1 && (
          <>
            <button
              type="button"
              className="cart-upsells__nav-button cart-upsells__nav-button--prev"
              onClick={() => handleClick(-1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="cart-upsells__nav-button cart-upsells__nav-button--next"
              onClick={() => handleClick(1)}
            >
              Next
            </button>
          </>
        )}
      </div>
    </div>
  ) : null;
};

class SideCart extends React.Component {
  constructor(props) {
    super(props);
    window.sideCart = this;
    this.updateCart = this.updateCart.bind(this);
    this.handleAddToCartEvent = this.handleAddToCartEvent.bind(this);
    this.handleUpdateCartEvent = this.handleUpdateCartEvent.bind(this);
    this.handleToggleUpsellsCollapse = this.handleToggleUpsellsCollapse.bind(this);
    this.handleTogglePairAndSaveCollapse = this.handleTogglePairAndSaveCollapse.bind(this);
    this.handleToggleBundleAndSaveCollapse = this.handleToggleBundleAndSaveCollapse.bind(this);
    this.handleBundleAndSaveAddToCartEvent = this.handleBundleAndSaveAddToCartEvent.bind(this);
    this.setBundleAndSaveItems = this.setBundleAndSaveItems.bind(this);
    this.handleAddToCartBundleAndSave = this.handleAddToCartBundleAndSave.bind(this);

    const upsellsCollapsed =
      window.innerWidth < window.theme.breakpoints.medium
        ? window.theme.cartUpsells.openOnMobile
          ? false
          : true
        : window.theme.cartUpsells.openOnDesktop
          ? false
          : true;

    const pairAndSaveCollapsed = window.innerWidth < window.theme.breakpoints.medium;
    const bundleAndSaveCollapsed = window.innerWidth < window.theme.breakpoints.medium;

    this.state = {
      total: window.theme.cart ? Number(window.theme.cart.total_price) : 0,
      cartItems: window.theme.cart ? window.theme.cart.items : [],
      upsellsEnabled: window.theme.cartUpsells ? window.theme.cartUpsells.enabled : false,
      upsellsProducts: window.theme.cartUpsells ? window.theme.cartUpsells.products : [],
      upsellsCollapsed: upsellsCollapsed,
      pairAndSaveCollapsed,
      bundleAndSaveCollapsed,
      rootUrl: window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '',
      freeShippingBar: window.theme.freeShipping ? window.theme.freeShipping.enabled : false,
      threshold: window.theme.freeShipping ? Number(window.theme.freeShipping.threshold) : 0,
      checkoutDisabled: false,
      openAssignConsultant: false,
      bundleAndSaveItems: [],
      changeStateUI: 0
    };
  }

  setCheckoutDisabled(state) {
    this.setState({
      checkoutDisabled: state,
    });
  }

  async updateCart(cart) {
    const cartData = await axios({
      method: 'GET',
      url: `${this.state.rootUrl}/cart?view=ajax`,
      responseType: 'JSON',
    })
      .then(response => {
        window.theme.initCart = response.data;
        return response.data
      })
      .catch(err => window.console.error('Error updating cart: ', err));

    cart = cartData ? cartData : cart

    this.setState({
      total: cart.total_price,
      cartItems: cart.items,
    });
    const headerCounter = document.querySelector('[data-items-count]');
    if (headerCounter) {
      headerCounter.innerHTML = cart.item_count;
      if (cart.item_count > 0) {
        headerCounter.classList.remove('cart-items-count--empty');
      } else {
        headerCounter.classList.add('cart-items-count--empty');
      }
    }
    const disableCart = this.checkAllItemsQtyRequirements(this.state.cartItemsTagDetails, this.state.cartItems);
    this.setCheckoutDisabled(disableCart);
  }

  handleAddToCartEvent(items, openCart = false, max = 0, handle = '', callback = null, isBundle, bundleProductObj) {

    // Had to timeout - will fix post launch, but this is a quick fix for now
    // Doesn't affect anything except the GA4 dataLayer push
    setTimeout(() => {
      // Push to Cart DataLayer GA4
      const itemID = items[0].id;
      // Check if there are items in the cart
      const itemsInCart = window.theme.initCart.items.length ? true : false
      if (itemsInCart) {
        // currentProduct && window.theme.cartUpsellProducts can exist on PDP page, which affects the handleAddToCartEvent
        // Have to check if this is an upsell item being added to cart or just from PDP
        window.theme.cartUpsellProducts = window.theme.cartUpsellProducts || [];

        if (window.theme.cartUpsellProducts && window.theme.cartUpsellProducts.length > 0) {
          const upsellItemBeingAdded = window.theme.cartUpsellProducts.find(product => product.variants.find(variant => variant.id === itemID));
          if (!upsellItemBeingAdded && typeof currentProduct !== 'undefined') {
            pushToCartEvent('ga_add_to_cart', itemID, currentProduct)

          }
          if (upsellItemBeingAdded) {
            pushToCartEvent('ga_add_to_cart', itemID, window.theme.cartUpsellProducts)
          }
        }
        // const upsellItemBeingAdded = window.theme.cartUpsellProducts.find(product => product.variants.find(variant => variant.id === itemID))
        // if (!upsellItemBeingAdded && typeof currentProduct !== 'undefined') {
        //   pushToCartEvent('ga_add_to_cart', itemID, currentProduct)

        // }
        // if (upsellItemBeingAdded) {
        //   pushToCartEvent('ga_add_to_cart', itemID, window.theme.cartUpsellProducts)
        // }
      }
    }, 2000)

    /**
     * If there is a max parameter set, check if the new quantity is less than the maximum.
     */
    const cartItems = this.state.cartItems;
    let isMaxed = false;
    if (items.length > 1) {
      items.forEach(itemToBeAdded => {
        if (itemToBeAdded.properties && itemToBeAdded.properties.hasOwnProperty('_item_max_quantity')) {
          let itemsMatchedInCart = cartItems.filter(cartItem => cartItem.variant_id == parseInt(itemToBeAdded.id));
          let itemQuantityToBeAdded = itemToBeAdded.quantity ? parseInt(itemToBeAdded.quantity) : 0;
          if ((itemQuantityToBeAdded + parseInt(itemsMatchedInCart.length)) > parseInt(itemToBeAdded.properties._item_max_quantity)) {
            isMaxed = true;
            return;
          }
        }
      });
      if (isMaxed) {
        window.console.log(`Max items in cart.`);
        return;
      }
    } else {
      if (max > 0) {
        const itemInCart = this.state.cartItems.find(item => item.handle === handle);
        if (itemInCart) {
          const newQuantity = itemInCart.quantity + items[0].quantity;
          if (newQuantity > max) {
            window.console.log(`Max ${max} items in cart.`);
            return;
          }
        }
      }
    }

    let phrase = '';
    let bundleItems = [];
    let thisFunction = this;
    let bundleEncryptKey = '';

    if (isBundle) {
      let bundleHandles = [];
      let bundleTagHandles = bundleProductObj.tags;
      const timestamp = Date.now().toString();
      let originalEncrypt = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890#%-.';
      originalEncrypt = originalEncrypt.split('');
      let cypherString = 'X8uP7t64sjHOEMg0nqGF-CQlBkU9b2SzKivVIDArN5WxmoLJwafpcRyd1hYZT%#e3.';
      cypherString = cypherString.split('');
      let encryptId = '';

      bundleTagHandles.forEach(function (handle, index) {
        if (handle.indexOf('bundle_item:') > -1) {
          let productHandle = handle.replace('bundle_item:', '');
          bundleHandles.push(productHandle);
        }
      });

      phrase = `${bundleProductObj.handle}${bundleProductObj.price}${bundleHandles.length}`;
      phrase = phrase.split('');

      phrase.forEach(function (letter) {
        originalEncrypt.forEach(function (origLetter, index) {
          if (origLetter == letter) {
            bundleEncryptKey = bundleEncryptKey.concat(cypherString[index]);
          }
        })
      });

      bundleHandles.forEach(function (handle, index) {
        axios({
          method: 'GET',
          url: `/products/${handle}.js`,
          responseType: 'JSON',
        }).then(response => {
          let productObj = response.data;
          let productToAdd = {}
          let preOrderDate = '';
          let maxInCart = '';
          if (productToAdd.tags) {
            productToAdd.tags.forEach(function (tag, index) {
              if (tag.indexOf('preorder') > -1) {
                preOrderDate = tag.replace('preorder:', '');
              }
              if (tag.indexOf('max:') > -1) {
                maxInCart = parseInt(tag.replace('max:', ''));
              }
            });
          }
          productToAdd.properties = {};
          productToAdd.id = productObj.variants[0].id;
          productToAdd.quantity = 1;
          productToAdd.properties['_bundle'] = `${bundleEncryptKey}${timestamp}`;
          productToAdd.properties['_bundle_items'] = parseInt(bundleHandles.length);
          productToAdd.properties['_bundle_handle'] = bundleProductObj.handle;
          productToAdd.properties['_bundle_price'] = bundleProductObj.price;
          productToAdd.properties['_bundle_title'] = bundleProductObj.title;
          productToAdd.properties['_bundle_image'] = getSizedImageUrl(bundleProductObj.featured_image, '160x160_crop_center');
          productToAdd.properties['_bundle_identifier'] = timestamp;
          if (preOrderDate && preOrderDate != '') {
            productToAdd.properties['Preorder'] = preOrderDate;
          }

          bundleItems.push(productToAdd);
        });
      });
      setTimeout(function () {
        axios({
          method: 'POST',
          url: `${thisFunction.state.rootUrl}/cart/add.js`,
          data: {
            items: bundleItems
          },
          responseType: 'JSON',
        })
          .then(() => {
            axios({
              method: 'GET',
              url: `${thisFunction.state.rootUrl}/cart.js`,
              responseType: 'JSON',
            })
              .then(response => {
                thisFunction.updateCart(response.data);
                if (openCart) {
                  thisFunction.handleCartToggle();
                }
                if (callback) {
                  callback();
                }
              })
              .catch(err => window.console.error('Error updating cart in handleAddToCartEvent: ', err));
          })
          .catch(err => window.console.error('Error adding bundle item in handleAddToCartEvent: ', err));
      }, 600);

    } else {
      axios({
        method: 'POST',
        url: `${this.state.rootUrl}/cart/add.js`,
        data: {
          items,
        },
        responseType: 'JSON',
      })
        .then(() => {
          axios({
            method: 'GET',
            url: `${this.state.rootUrl}/cart.js`,
            responseType: 'JSON',
          })
            .then(response => {
              this.updateCart(response.data);
              if (openCart) {
                this.handleCartToggle();
              }
              if (callback) {
                callback();
              }
              document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: response.data?.items
              }))
            })
            .catch(err => window.console.error('Error updating cart in HandleAddToCart: ', err));
        })
        .catch(err => window.console.error('Error adding to cart in HandleAddToCart: ', err));
    }
  }

  setBundleAndSaveItems(bundleAndSaveObj) {
    this.setState({
      bundleAndSaveItems: bundleAndSaveObj
    });
  }

  handleBundleAndSaveAddToCartEvent(items, openCart = false, max = 0, handle = '', callback = null) {
    /**
     * If there is a max parameter set, check if the new quantity is less than the maximum.
     */
    if (max > 0) {
      const itemInCart = this.state.cartItems.find(item => item.handle === handle);
      if (itemInCart) {
        const newQuantity = itemInCart.quantity + items[0].quantity;
        if (newQuantity > max) {
          window.console.log(`Max ${max} items in cart.`);
          return;
        }
      }
    }

    const cartItems = this.state.cartItems;
    const bundleAndSaveArray = this.state.bundleAndSaveItems;
    const timestamp = Date.now().toString();
    let bundleItemsObj = [];
    let removeItems = [];
    let bundleParentIds = [];

    cartItems.map(cartItem => {
      let itemKey = cartItem.key;
      let itemQuantity = cartItem.quantity;
      let lineVariantId = cartItem.variant_id;
      let bundleData = bundleAndSaveArray.filter(bundleObj => bundleObj.productId === cartItem.product_id);
      if (bundleData.length == 0) {
        return;
      }
      let bundleItems = bundleData[0].parentBundleProduct.bundle_products;
      let bundleItem = bundleItems.filter(bundleItem => lineVariantId == bundleItem.bundle_item_variantid);

      let hasUpsellItem = cartItems.filter(upsellObj => upsellObj.properties && upsellObj.properties.hasOwnProperty('_upsell_conditional_id') && upsellObj.properties._upsell_conditional_id == parseInt(bundleItem[0].bundle_item_productid));
      if (hasUpsellItem[0]) {
        let removeItem = {
          quantity: 0,
          id: hasUpsellItem[0].key
        }
        removeItems.push(removeItem);
      }

      if (bundleItem) {
        let removeItem = {
          quantity: parseInt(itemQuantity) - parseInt(bundleItem[0].bundle_item_quantity),
          id: itemKey
        }
        removeItems.push(removeItem);
      }

      if (bundleItems && !bundleParentIds.includes(parseInt(bundleData[0].parentBundleProduct.id))) {
        bundleParentIds.push(parseInt(bundleData[0].parentBundleProduct.id));
        bundleItems.map(bundleItem => {
          let updateObj = {
            quantity: parseInt(bundleItem.bundle_item_quantity),
            id: parseInt(bundleItem.bundle_item_variantid),
            properties: {
              "_bundle": `${bundleData[0].parentBundleId}${timestamp}`,
              "_bundle_items": bundleItems.length,
              "_bundle_handle": bundleItem.bundle_item_handle,
              "_bundle_price": bundleItem.bundle_price,
              "_bundle_title": bundleItem.bundle_title,
              "_bundle_image": bundleItem.bundle_image,
              "_bundle_dc_type": bundleItem.bundle_item_dc_type,
              "_bundle_dc_value": bundleItem.bundle_item_dc_value,
              "_bundle_identifier": timestamp,
              "_item_max_quantity": bundleItem.bundle_item_max_quantity,
              "_disable_pair_and_save": bundleItem.disable_pair_and_save,
              "_disable_gwp": bundleItem.disable_gwp
            }
          };
          if (bundleItem.bundle_item_preorder && bundleItem.bundle_item_preorder != '') {
            updateObj['properties'].Preorder = bundleItem.bundle_item_preorder;
          }
          bundleItemsObj.push(updateObj);
        });
      }
    });
    this.handleAddToCartBundleAndSave(bundleItemsObj, removeItems);
  }

  async handleAddToCartBundleAndSave(updates, removeItems) {
    await axios({
      method: 'POST',
      url: `${this.state.rootUrl}/cart/add.js`,
      data: {
        items: updates
      },
      responseType: 'JSON'
    })
      .then(() => {
        let itemsToBeRemovedArray = [];
        let removeObj = {};
        removeItems.map(itemToBeRemoved => {
          let itemToBeRemovedString = itemToBeRemoved.id;
          itemsToBeRemovedArray.push(itemToBeRemovedString);
        });
        itemsToBeRemovedArray.forEach(function (item, index) {
          removeObj[item] = 0;
        });
        axios({
          method: 'POST',
          url: `${this.state.rootUrl}/cart/update.js`,
          data: {
            updates: removeObj
          },
          responseType: 'JSON'
        })
          .then(() => {
            axios({
              method: 'GET',
              url: `${this.state.rootUrl}/cart.js`,
              responseType: 'JSON',
            })
              .then(response => {
                this.updateCart(response.data);
                // this.handleCartToggle();
              })
              .catch(err => window.console.error('Error updating cart in handleAddToCartBundleAndSave: ', err));
          });
      })
      .catch(err => window.console.error('Error in handleAddToCartBundleAndSave: ', err));
  }

  async handleUpdateCartEvent(updates) {
    const response = await axios({
      method: 'POST',
      url: `${this.state.rootUrl}/cart/update.js`,
      data: {
        updates,
      },
      responseType: 'JSON',
    });
    this.updateCart(response.data);
    if (window.location.pathname == '/cart') {
      window.location.reload();
    }
  }

  handleCartToggle(event) {
    if (event) {
      event.preventDefault();
    }
    document.body.classList.toggle('cart-is-visible');
  }

  handleToggleUpsellsCollapse(state) {
    this.setState({
      upsellsCollapsed: state !== undefined ? state : !this.state.upsellsCollapsed,
    });
  }

  handleTogglePairAndSaveCollapse(state) {
    this.setState({
      pairAndSaveCollapsed: state !== undefined ? state : !this.state.pairAndSaveCollapsed,
    });
  }

  handleToggleBundleAndSaveCollapse(state) {
    this.setState({
      bundleAndSaveCollapsed: state !== undefined ? state : !this.state.bundleAndSaveCollapsed,
    });
  }

  /**
   * Checks if any of the cart items have a minumim or maxmium quantity requirement.
   * @returns {boolean} true if any items don't meat the minimum or maximum quantity requirement.
   */
  checkAllItemsQtyRequirements(cartItemsTagDetails, cartItems) {
    if (!Array.isArray(cartItemsTagDetails)) {
      console.log('cartItemsTagDetails is not an array', cartItemsTagDetails);
      return false;
    }
    return cartItemsTagDetails.some(({ id, min, max }) => {
      if (min === 0 && max === 0) return false;
      const currentItem = cartItems.find(item => item.product_id === id);
      if (!currentItem) return false;
      if (currentItem.quantity < min || currentItem.quantity > max) return true;
      return false;
    });
  }

  /**
   * Fetches the product data for the cart items using the AJAX API.
   * We need to check for the existence of min:x and max:x tags to determine
   * quantity limits and a tm_machine tag to determine whether the item is a machine.
   *
   * @returns {Array} An array of id, min and max.
   */
  async getItemsTagDetails(items) {
    const itemPromises = items.map(async item => {
      const { handle } = item;
      const productFetch = await fetch(`/products/${handle}?view=cart-item-details`);
      const productData = await productFetch.json();
      const maxTag = productData.tags.find(tag => tag.includes('max:'));
      const minTag = productData.tags.find(tag => tag.includes('min:'));
      const maxQuantity = maxTag ? parseInt(maxTag.replace('max:', '')) : 0;
      const minQuantity = minTag ? parseInt(minTag.replace('min:', '')) : 0;
      const isMachine = productData.tags.includes('tm_machine');
      const inclusions = productData.inclusions ? productData.inclusions : [];
      return {
        id: item.product_id,
        min: minQuantity,
        max: maxQuantity,
        isMachine,
        inclusions,
      };
    });
    const itemTagDetails = await Promise.all(itemPromises);
    return itemTagDetails;
  }

  async componentDidMount() {
    const cartToggle = document.querySelectorAll('[data-toggle-cart]');
    if (cartToggle.length > 0) {
      cartToggle.forEach(el => el.addEventListener('click', this.handleCartToggle));
    }

    /**
     * If cart is empty remove 'removedAutoUpsell' local storage value
     */
    if (this.state.cartItems.length === 0) {
      window.localStorage.removeItem('removedAutoUpsell');
    }

    /**
     * On mount, we get the min and max value from product tags.
     *
     * min is used on the cart level, to check if any of the items don't meet
     * minimum quantity requierments. The checkout button is disabled if so.
     *
     * max is used in each CartItem line component.
     */
    // const cartItemsTagDetails = await this.getItemsTagDetails(this.state.cartItems);

    let cartItemsTagDetails;

    try {
      cartItemsTagDetails = await this.getItemsTagDetails(this.state.cartItems);
    } catch (error) {
      console.error('An error occurred getting Item Tag Details: ', error, 'this.state.cartItems: ', this.state.cartItems);
    }

    this.setState({
      cartItemsTagDetails,
    });

    /**
     * Check if any items are above their max quantity value.
     * If so we need to decrease the qty.
     */
    const itemsAboveMaxQty = this.state.cartItems.filter(item => {
      const itemDetails = cartItemsTagDetails.find(({ id }) => id === item.product_id);
      if (!itemDetails) return false;
      if (itemDetails.max === 0) return false;
      if (item.quantity > itemDetails.max) return true;
      return false;
    });

    /**
     * Check if the cart contains upsells that are no longer being offered.
     * If so we need to remove them.
     */
    const invalidUpsells = this.state.cartItems.filter(item => {
      const isNewUpsell = Boolean(item.properties && item.properties._new_upsell)
      if (isNewUpsell) {
        return
      }

      const isUpsell = Boolean(item.properties && item.properties._upsell_conditional_id);
      if (!isUpsell) {
        return false;
      }
      const isValid = Boolean(
        item.properties &&
        item.properties._upsell_conditional_hash &&
        window.theme.cartUpsells.products.some(
          ({ conditionalHash }) => conditionalHash === item.properties._upsell_conditional_hash
        )
      );
      const cartHasConditionalProduct = this.state.cartItems.some(
        currentItem => currentItem.product_id === item.properties._upsell_conditional_id
      );
      return !isValid || !cartHasConditionalProduct;
    });

    if (itemsAboveMaxQty.length > 0 || invalidUpsells.length > 0) {
      const updates = {};

      itemsAboveMaxQty.forEach(item => {
        const itemDetails = cartItemsTagDetails.find(({ id }) => id === item.product_id);
        updates[item.key] = itemDetails.max;
      });

      invalidUpsells.forEach(item => {
        updates[item.key] = 0;
      });

      this.handleUpdateCartEvent(updates);
    }

    const disableCart = this.checkAllItemsQtyRequirements(cartItemsTagDetails, this.state.cartItems);
    this.setCheckoutDisabled(disableCart);

    let thisFunction = this;

    eventBus.on('eventUpdateConsultant', function () {
      // change state to update UI
      thisFunction.setState({
        changeStateUI: thisFunction.state.changeStateUI += 1
      });
    });
  }

  async componentDidUpdate(prevProps, prevState) {
    /**
     * On state change, we see if the cart items have changed. If so we update the
     * cartItemsTagDetails state value.
     *
     * min is used on the cart level, to check if any of the items don't meet
     * minimum quantity requierments. The checkout button is disabled if so.
     *
     * max is used in each CartItem line component.
     */
    if (this.state.cartItems.length !== prevState.cartItems.length) {
      /**
       * If cart is empty remove 'removedAutoUpsell' local storage value
       */
      if (this.state.cartItems.length === 0) {
        window.localStorage.removeItem('removedAutoUpsell');
      }

      const cartItemsTagDetails = await this.getItemsTagDetails(this.state.cartItems);
      this.setState({
        cartItemsTagDetails,
      });

      /**
       * Check if any items are above their max quantity value.
       * If so we need to decrease the qty.
       */
      const itemsAboveMaxQty = this.state.cartItems.filter(item => {
        const itemDetails = cartItemsTagDetails.find(({ id }) => id === item.product_id);
        if (!itemDetails) return false;
        if (itemDetails.max === 0) return false;
        if (item.quantity > itemDetails.max) return true;
        return false;
      });

      /**
       * Check if the cart contains upsells that are no longer being offered.
       * If so we need to remove them.
       */
      const invalidUpsells = this.state.cartItems.filter(item => {
        const isNewUpsell = Boolean(item.properties && item.properties._new_upsell)
        if (isNewUpsell) {
          return
        }

        const isUpsell = Boolean(item.properties && item.properties._upsell_conditional_id);
        if (!isUpsell) {
          return false;
        }
        const isValid = Boolean(
          item.properties &&
          item.properties._upsell_conditional_hash &&
          window.theme.cartUpsells.products.some(
            ({ conditionalHash }) => conditionalHash === item.properties._upsell_conditional_hash
          )
        );
        const cartHasConditionalProduct = this.state.cartItems.some(
          currentItem => currentItem.product_id === item.properties._upsell_conditional_id
        );
        return !isValid || !cartHasConditionalProduct;
      });

      if (itemsAboveMaxQty.length > 0 || invalidUpsells.length > 0) {
        const updates = {};

        itemsAboveMaxQty.forEach(item => {
          const itemDetails = cartItemsTagDetails.find(({ id }) => id === item.product_id);
          updates[item.key] = itemDetails.max;
        });

        invalidUpsells.forEach(item => {
          updates[item.key] = 0;
        });

        this.handleUpdateCartEvent(updates);
      }

      const disableCart = this.checkAllItemsQtyRequirements(cartItemsTagDetails, this.state.cartItems);
      this.setCheckoutDisabled(disableCart);
    }
  }

  checkTradeUpPage() {
    const checkTradeUpPage = document.querySelector('.page-trade-up');
    if (checkTradeUpPage == null) {
      return false;
    } else {
      return true;
    }
  }

  render() {
    const {
      total,
      cartItems,
      rootUrl,
      freeShippingBar,
      threshold,
      upsellsEnabled,
      upsellsProducts,
      upsellsCollapsed,
      pairAndSaveCollapsed,
      bundleAndSaveCollapsed,
      openAssignConsultant,
    } = this.state;

    const bundlesShown = [];
    const hasTM6 = this.state.cartItemsTagDetails && this.state.cartItemsTagDetails.some(item => item.isMachine);
    const shouldShowCheckoutLink = getFromStorage('consultant_id', 'local')
    let notMaxed = true;
    cartItems.forEach(item => {
      if (item.properties && item.properties.hasOwnProperty('_item_max_quantity')) {
        let itemsMatchedInCart = cartItems.filter(cartItem => cartItem.variant_id == parseInt(item.id));
        let itemQuantityToBeAdded = item.quantity ? parseInt(item.quantity) : 0;
        if ((itemQuantityToBeAdded + parseInt(itemsMatchedInCart.length)) > parseInt(item.properties._item_max_quantity)) {
          notMaxed = false;
          return false;
        }
      }
    });

    const { productCondition } = window.newCartUpsellConfig
    const isHasNewUpsell = this.state.cartItems.some(item => item.product_id === productCondition.id)

    document.addEventListener("cart:updated", (detail) => {
      const isConditionUpsel = detail.detail.some(item => item.product_id === productCondition.id)
      const productUpsell = detail.detail.find(item => item.properties?._new_upsell)

      if (!isConditionUpsel && productUpsell?.id) {
        const updates = {
          [productUpsell.key]: 0
        }
        this.handleUpdateCartEvent(updates)
      }
    });

    return (
      <React.Fragment>
        <div className="side-cart__header">
          <button
            type="button"
            className="side-cart__close"
            aria-label={window.theme.strings.closeCart}
            data-toggle-cart
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
              <g fill="none" fillRule="evenodd" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 4l12 12M16 4L4 16" />
              </g>
            </svg>
          </button>
          <h4 className="side-cart__heading">{window.theme.strings.cartTitle}</h4>
          {freeShippingBar && <FreeShippingBar total={total} threshold={threshold} />}
        </div>

        {cartItems.length ? (
          <div className="side-cart__content">
            <ul className="cart-items__container list-none">
              {cartItems.map(item => {
                if (item.properties && item.properties._bundle) {
                  if (bundlesShown.includes(item.properties._bundle)) {
                    return null;
                  }
                  bundlesShown.push(item.properties._bundle);
                  return (
                    <CartBundleItem
                      item={item}
                      cartItems={cartItems}
                      cartItemsTagDetails={this.state.cartItemsTagDetails}
                      updateCart={this.updateCart}
                      key={item.key}
                    />
                  );
                }
                return (
                  <CartItem
                    item={item}
                    cartItemsTagDetails={this.state.cartItemsTagDetails}
                    updateCart={this.updateCart}
                    key={item.key}
                  />
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="side-cart__empty">
            <p>
              <svg
                className="icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.00001 4V1C5.00001 0.734784 5.10536 0.48043 5.2929 0.292893C5.48044 0.105357 5.73479 0 6.00001 0H14C14.2652 0 14.5196 0.105357 14.7071 0.292893C14.8947 0.48043 15 0.734784 15 1V4H17.788C18.0394 3.99995 18.2816 4.09459 18.4664 4.26508C18.6511 4.43557 18.7649 4.6694 18.785 4.92L19.997 18.92C20.0081 19.0576 19.9905 19.1961 19.9454 19.3266C19.9003 19.4571 19.8287 19.5768 19.735 19.6783C19.6413 19.7798 19.5277 19.8607 19.4012 19.9161C19.2747 19.9715 19.1381 20 19 20H1.00001C0.861926 20 0.725335 19.9715 0.59884 19.9161C0.472344 19.8607 0.358686 19.7798 0.265024 19.6783C0.171363 19.5768 0.0997282 19.4571 0.0546333 19.3266C0.00953836 19.1961 -0.00803963 19.0576 0.00300685 18.92L1.21501 4.92C1.23512 4.6694 1.34888 4.43557 1.53364 4.26508C1.7184 4.09459 1.9606 3.99995 2.21201 4H5.00001ZM7.00001 4H13V2H7.00001V4ZM5.00001 6H3.13501L2.08501 18H17.915L16.865 6H15V7C15 7.26522 14.8947 7.51957 14.7071 7.70711C14.5196 7.89464 14.2652 8 14 8C13.7348 8 13.4804 7.89464 13.2929 7.70711C13.1054 7.51957 13 7.26522 13 7V6H7.00001V7C7.00001 7.26522 6.89465 7.51957 6.70711 7.70711C6.51958 7.89464 6.26522 8 6.00001 8C5.73479 8 5.48044 7.89464 5.2929 7.70711C5.10536 7.51957 5.00001 7.26522 5.00001 7V6Z"
                  fill="currentColor"
                />
              </svg>
            </p>
            <p>{window.theme.strings.empty}</p>
            <p dangerouslySetInnerHTML={{ __html: window.theme.strings.continueBrowsingHTML }} />
          </div>
        )}

        <div className={`side-cart__footer ${cartItems.length === 0 ? 'hide' : ''}`}>
          {cartItems.length > 0 && cartItems.some(item => !item.properties['_bundle_dc_type']) && notMaxed && (
            <BundleAndSaves
              cartItems={cartItems}
              isCollapsed={bundleAndSaveCollapsed}
              handleToggleBundleAndSaveCollapse={this.handleToggleBundleAndSaveCollapse}
              toggleUpsellsCollapse={this.handleToggleUpsellsCollapse}
              handleBundleAndSaveAddToCartEvent={this.handleBundleAndSaveAddToCartEvent}
              setBundleAndSaveItems={this.setBundleAndSaveItems}
            />
          )}
          {cartItems.length > 0 && upsellsEnabled && (
            <CartUpsells
              cartItems={cartItems}
              upsellsProducts={upsellsProducts}
              isCollapsed={upsellsCollapsed}
              toggleUpsellsCollapse={this.handleToggleUpsellsCollapse}
              handleAddToCartEvent={this.handleAddToCartEvent}
            />
          )}
          {cartItems.length > 0 && cartItems.some(item => item.properties['_disable_pair_and_save'] == 'false') && (
            <PairAndSaves
              cartItems={cartItems}
              isCollapsed={pairAndSaveCollapsed}
              handleTogglePairAndSaveCollapse={this.handleTogglePairAndSaveCollapse}
              toggleUpsellsCollapse={this.handleToggleUpsellsCollapse}
              handleAddToCartEvent={this.handleAddToCartEvent}
            />
          )}
          <div className="side-cart__subtotal">
            <span className="side-cart__subtotal-label">{window.theme.strings.subtotal}</span>
            <span className="side-cart__subtotal-value">{currency.formatMoney(total, window.theme.moneyFormat)}</span>
          </div>
          <div className="side-cart__shipping-note">{window.theme.strings.shippingAtCheckout}</div>
          {this.checkTradeUpPage() && (
            <div className="side-cart__consultant" data-consultant data-side-cart-consultant>
            </div>
          )}
          {this.checkTradeUpPage() == false && (
            <div className="side-cart__consultant">
              <Consultant
                showCheckout={!this.state.checkoutDisabled}
                openAssignConsultant={openAssignConsultant}
              />
            </div>
          )}
          <form action={'/cart'} method={'post'} className="side-cart__checkout">
            {
              shouldShowCheckoutLink ?
                <button
                  type={'submit'}
                  name={'checkout'}
                  className={`btn btn--secondary btn--block 1 ${this.state.checkoutDisabled ? 'disabled' : ''}`}
                >
                  {window.theme.strings.checkout}
                </button>
                :
                <button
                  className={`btn btn--secondary btn--block 2 ${this.state.checkoutDisabled ? 'disabled' : ''}`}
                  type={'submit'}
                  name={'checkout'}
                  onClick={(e) => {
                    if (isHasNewUpsell) {
                      e.preventDefault()

                      this.handleCartToggle()
                      document.dispatchEvent(new CustomEvent('open-modal-upsell'))
                    } else {
                      const promptToAddConsultant = !(sessionStorage.getItem('promptToAddConsultant') === 'false');
                      if (promptToAddConsultant) {
                        if (this.checkTradeUpPage() == false) {
                          e.preventDefault()
                        }
                      }
                      const isEnabled = window.theme.consultants.enabled && promptToAddConsultant;
                      const isDisabledHasTM6 = !window.theme.consultants.enabled && hasTM6 && promptToAddConsultant;
  
                      if (isEnabled || isDisabledHasTM6) {
                        this.setState({
                          openAssignConsultant: true,
                        });
                      }
                    }
                  }}
                >
                  {window.theme.strings.checkout}
                </button>
            }
          </form>

        </div>
      </React.Fragment>
    );
  }
}

FreeShippingBar.propTypes = {
  total: PropTypes.number,
  threshold: PropTypes.number,
};

CartUpsells.propTypes = {
  cartItems: PropTypes.array,
  upsellsProducts: PropTypes.array,
  isCollapsed: PropTypes.bool,
  toggleUpsellsCollapse: PropTypes.func,
  handleAddToCartEvent: PropTypes.func,
};

CartUpsell.propTypes = {
  featured_image: PropTypes.string,
  title: PropTypes.string,
  handle: PropTypes.string,
  url: PropTypes.string,
  price: PropTypes.number,
  price_min: PropTypes.number,
  compare_at_price: PropTypes.number,
  price_varies: PropTypes.bool,
  variants: PropTypes.arrayOf(PropTypes.object),
  tags: PropTypes.arrayOf(PropTypes.string),
  handleAddToCartEvent: PropTypes.func,
  cartItems: PropTypes.arrayOf(PropTypes.object),
};

export default SideCart;
